import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Game Overview', path: '/game-overview' },
  { label: 'Level Selector', path: '/level-selector' },
];

const CHALLENGE_LINKS = [
  { label: 'Multiplication', path: '/multiplication-challenge' },
  { label: 'Volume', path: '/volume-challenge' },
  { label: 'Division', path: '/division-challenge' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [challengeOpen, setChallengeOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setChallengeOpen(false);
    setMobileOpen(false);
  }, [location]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <img
            src="https://public.readdy.ai/ai/img_res/6db66753-9ba6-419d-8cc5-2b89b104d003.png"
            alt="Math Tennis Logo"
            className="h-10 w-auto object-contain"
          />
          <span
            className={`font-bold text-lg tracking-tight whitespace-nowrap transition-colors ${
              scrolled ? 'text-green-800' : 'text-white'
            }`}
            style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.4rem', letterSpacing: '0.05em' }}
          >
            MATH TENNIS
          </span>
        </Link>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map(link => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  isActive(link.path)
                    ? scrolled ? 'text-green-700 font-semibold' : 'text-lime-300 font-semibold'
                    : scrolled ? 'text-gray-700 hover:text-green-700' : 'text-white/90 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            </li>
          ))}

          {/* Online mode link */}
          <li>
            <Link
              to="/online-game"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                isActive('/online-game')
                  ? scrolled ? 'text-sky-600 font-semibold' : 'text-sky-300 font-semibold'
                  : scrolled ? 'text-gray-700 hover:text-sky-600' : 'text-white/90 hover:text-white'
              }`}
            >
              <i className="ri-global-line text-base" />
              Online
            </Link>
          </li>

          {/* Challenges dropdown */}
          <li className="relative">
            <button
              onClick={() => setChallengeOpen(o => !o)}
              className={`flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-green-700' : 'text-white/90 hover:text-white'
              }`}
            >
              Challenges
              <i className={`ri-arrow-down-s-line text-base transition-transform ${challengeOpen ? 'rotate-180' : ''}`} />
            </button>
            {challengeOpen && (
              <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-md py-1 min-w-44 border border-gray-100">
                {CHALLENGE_LINKS.map(c => (
                  <Link
                    key={c.path}
                    to={c.path}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 cursor-pointer"
                  >
                    {c.label} Challenge
                  </Link>
                ))}
              </div>
            )}
          </li>
        </ul>

        {/* CTA */}
        <Link
          to="/level-selector"
          className="hidden md:block px-5 py-2 rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer transition-all bg-lime-400 text-green-900 hover:bg-lime-300"
        >
          Play Now
        </Link>

        {/* Mobile hamburger */}
        <button
          className={`md:hidden text-2xl cursor-pointer ${scrolled ? 'text-gray-700' : 'text-white'}`}
          onClick={() => setMobileOpen(o => !o)}
        >
          <i className={mobileOpen ? 'ri-close-line' : 'ri-menu-line'} />
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-2">
          {NAV_LINKS.map(link => (
            <Link
              key={link.path}
              to={link.path}
              className="text-gray-700 font-medium py-2 cursor-pointer hover:text-green-700"
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/online-game"
            className="text-gray-700 font-medium py-2 cursor-pointer hover:text-sky-600 flex items-center gap-1.5"
          >
            <i className="ri-global-line" />
            Online Match
          </Link>
          <div className="border-t border-gray-100 pt-2 mt-1">
            <p className="text-xs text-gray-400 font-medium mb-1">Challenges</p>
            {CHALLENGE_LINKS.map(c => (
              <Link
                key={c.path}
                to={c.path}
                className="text-gray-700 py-2 block cursor-pointer hover:text-green-700"
              >
                {c.label} Challenge
              </Link>
            ))}
          </div>
          <Link
            to="/level-selector"
            className="mt-2 text-center px-5 py-2 rounded-full font-semibold bg-lime-400 text-green-900 cursor-pointer"
          >
            Play Now
          </Link>
        </div>
      )}
    </header>
  );
}
